import type { Endpoint } from '../../features/catalog/model';

/** An endpoint's published sample response as spreadsheet rows. */
export interface SampleTable {
  columns: string[];
  rows: string[][];
}

type Row = Record<string, string>;

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parse = (json?: string): unknown => {
  if (!json) return null;
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
};

/** The payload under the gateway's `{ found, data }` envelope, when there is one. */
const payloadOf = (root: unknown): unknown =>
  isObject(root) && 'data' in root ? root.data : root;

/** The list a list endpoint returns: the payload itself, or its first member
 *  that is an array of objects (`comments`, `posts`, ...). */
const listOf = (payload: unknown): Record<string, unknown>[] | null => {
  const objects = (value: unknown) =>
    Array.isArray(value) && value.length > 0 && value.every(isObject)
      ? (value as Record<string, unknown>[])
      : null;
  if (Array.isArray(payload)) return objects(payload);
  if (!isObject(payload)) return null;
  for (const member of Object.values(payload)) {
    const list = objects(member);
    if (list) return list;
  }
  return null;
};

const cell = (value: unknown): string =>
  value === null || value === undefined
    ? ''
    : typeof value === 'string'
      ? value
      : String(value);

/** One item flattened to leaf columns: nested objects as dotted names, arrays
 *  of plain values joined, arrays of objects left out (they are not one cell). */
const flatten = (value: Record<string, unknown>, prefix: string, row: Row): Row => {
  for (const [key, member] of Object.entries(value)) {
    const name = prefix ? `${prefix}.${key}` : key;
    if (isObject(member)) flatten(member, name, row);
    else if (Array.isArray(member)) {
      if (!member.some((item) => item !== null && typeof item === 'object')) {
        row[name] = member.map(cell).join(', ');
      }
    } else row[name] = cell(member);
  }
  return row;
};

/** The sample's list items (or its single object, as one row) flattened to
 *  leaf columns, in first-seen column order. Null when there is no sample. */
export const sampleRows = (endpoint: Endpoint): SampleTable | null => {
  const payload = payloadOf(parse(endpoint.sampleResponse));
  const items = listOf(payload) ?? (isObject(payload) ? [payload] : null);
  if (!items) return null;
  const flat = items.map((item) => flatten(item, '', {}));
  const columns = [...new Set(flat.flatMap((row) => Object.keys(row)))];
  if (columns.length === 0) return null;
  return { columns, rows: flat.map((row) => columns.map((name) => row[name] ?? '')) };
};

const csvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

/** The sample as a CSV file, as a `data:` URL an `<a download>` can save. */
export const sampleCsvHref = (endpoint: Endpoint): string | null => {
  const table = sampleRows(endpoint);
  if (!table) return null;
  const csv = [table.columns, ...table.rows]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n');
  return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
};

/** The published sample response, verbatim, as a JSON `data:` URL. */
export const sampleJsonHref = (endpoint: Endpoint): string | null =>
  parse(endpoint.sampleResponse) === null
    ? null
    : `data:application/json,${encodeURIComponent(endpoint.sampleResponse ?? '')}`;

/** `instagram.post_comments` + `csv` -> `anyapi-instagram-post-comments-sample.csv`. */
export const sampleFileName = (endpoint: Endpoint, ext: string): string =>
  `anyapi-${endpoint.sku.replace(/[._]/g, '-')}-sample.${ext}`;
